import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
export default function HeroSlider() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/unnamed (1).jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/unnamed (1).jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/unnamed (1).jpg-wvc-sizes"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/unnamed.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/unnamed.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1695754/img/unnamed.jpg-wvc-sizes"
  }];
  const testimonials = [{
    quote: "Many thanks to the Fabco Industrial Construction team for responding very quickly to our needs on the Variety Pack Automation project at MillerCoors. You have always stepped up above and beyond.",
    name: "Dale Reynebeau",
    title: "Project Manager | Foth Production Solutions, LLC"
  }, {
    quote: "Thank you FABCO for your quality millwright and process pipe work at our new brewery in Mills River NC. Your professionalism and craftsmanship is exemplary.",
    name: "Addison Williams",
    title: "Sierra Nevada Brewing CO."
  }, {
    quote: "The success of the Laser Guided Vehicle project in our new warehouse addition is due in large part to the timely, quality workmanship provided by your team in the demolition, millwright, process pipe and fabrication services provided. Thanks FABCO for another quality project.",
    name: "Jeffrey J. Zarka",
    title: "Sr. Project Engineer | MillerCoors"
  }];
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [slides.length]);
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };
  return <section ref={sectionRef} className="dark bg-background text-foreground font-sans overflow-hidden">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@300;400;500;600&display=swap');`}</style>

      <div className="relative w-full h-screen min-h-[600px] max-h-[900px] overflow-hidden group">
        
        {slides.map((slide, i) => <div key={i} data-index={i} className="absolute inset-0 transition-opacity duration-1000" style={{
        opacity: i === currentSlide ? 1 : 0
      }}>
            <img src={slide.src} data-wvc-srcset={slide.srcset} data-wvc-sizes={slide.sizes} className="w-full h-full object-cover" alt={`FABCO slide ${i + 1}`} loading="lazy" decoding="async" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          </div>)}

        {}
        <div className="absolute inset-y-0 left-4 z-20 flex items-center">
          <Button variant="ghost" size="icon" onClick={prevSlide} className="text-white hover:bg-white/20 rounded-lg transition-all opacity-0 group-hover:opacity-100">
            <ChevronLeft className="w-8 h-8" />
          </Button>
        </div>
        <div className="absolute inset-y-0 right-4 z-20 flex items-center">
          <Button variant="ghost" size="icon" onClick={nextSlide} className="text-white hover:bg-white/20 rounded-lg transition-all opacity-0 group-hover:opacity-100">
            <ChevronRight className="w-8 h-8" />
          </Button>
        </div>

        <div className="absolute inset-0 flex flex-col justify-center px-10 md:px-20 lg:px-32 transition-all duration-1000 opacity-100 translate-y-0">
          <div className="max-w-3xl flex flex-col items-start text-left">
            <div className="flex items-center gap-3 mb-6">
              
              
            </div>

            <h1 className="text-6xl md:text-8xl lg:text-9xl text-white leading-none mb-4 tracking-tight" style={{
            fontFamily: "'Bebas Neue', cursive"
          }}>
              FABCO’s mission
            </h1>

            <p className="text-white text-lg md:text-xl max-w-2xl leading-tight mb-8" style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 400
          }}>
              Complete all projects on time, within budget, and with the highest level of quality, safety, affordability and customer satisfaction.
            </p>
          </div>

          <div className="max-w-3xl flex flex-col items-end text-right self-end">
            <h2 className="text-4xl md:text-5xl lg:text-6xl text-white leading-none mb-6 tracking-tight" style={{
            fontFamily: "'Bebas Neue', cursive"
          }}>
              Safety is
              <br />
              <span className="text-orange-500">JOB ONE.</span>
            </h2>

            <p className="text-white text-base md:text-lg max-w-xl leading-relaxed mb-4" style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300
          }}>At ABCO South, safety is not just a priority—it is part of our culture. We expect every team member, from leadership to field personnel, to take ownership of maintaining a safe and compliant work environment on every project.</p>

            <p className="text-white text-base md:text-lg max-w-lg leading-relaxed mb-4" style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300
          }}>Our winning safety program has resulted in EMR rates well below the industry average.</p>

            <p className="text-white text-base md:text-lg max-w-lg leading-relaxed mb-10" style={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: 300
          }}>
              Fabco is headquarted in High Point, NC performing projects throughout the Southeast.
            </p>

            
          </div>
        </div>

        <div className="absolute bottom-10 left-10 md:left-20 lg:left-32 flex items-center gap-6">
          <div className="flex items-center gap-2">
            {slides.map((_, i) => <div key={i} data-index={i} className={`transition-all duration-300 rounded-lg ${i === currentSlide ? "w-8 h-2 bg-orange-500" : "w-2 h-2 bg-white/30"}`} />)}
          </div>
        </div>

      </div>
    </section>;
}